import {HttpClient} from "@angular/common/http"
import {Component, inject, input, resource, ChangeDetectionStrategy} from "@angular/core"
import {DomSanitizer} from "@angular/platform-browser"
import DOMPurify from "dompurify"
import {firstValueFrom} from "rxjs"
import {LoadingSpinnerIconComponent} from "@app/common/components/progress"

// Only fetch + inline SVGs hosted on Google Cloud Storage. Customers can configure the loading
// spinner URL on a template, so without this guard a malicious template author could point the
// inlining loader at an attacker-controlled host serving an SVG with embedded <script>.
const SVG_HOST_ALLOWLIST = new Set(["storage.googleapis.com"])

const isAllowedSvgUrl = (raw: string): boolean => {
    try {
        const url = new URL(raw)
        return url.protocol === "https:" && SVG_HOST_ALLOWLIST.has(url.hostname)
    } catch {
        return false
    }
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-custom-loading-screen",
    templateUrl: "./custom-loading-screen.component.html",
    styleUrls: ["./custom-loading-screen.component.scss"],
    imports: [LoadingSpinnerIconComponent],
    host: {
        "[class.cm-transparent]": "$transparent()",
    },
})
export class CustomLoadingScreenComponent {
    readonly $backgroundImageUrl = input<string | undefined>(undefined, {
        alias: "backgroundImageUrl",
    })
    readonly $loadingSpinnerSvgUrl = input<string | null | undefined>(undefined, {
        alias: "loadingSpinnerSvgUrl",
    })
    readonly $showSpinner = input.required<boolean>({
        alias: "showSpinner",
    })
    readonly $transparent = input<boolean>(false, {
        alias: "transparent",
    })

    // In order to display arbitrary loading animations using prepared SVGs uploaded to the platfrom
    // we need to fetch the svg and inline it into the DOM (img element won't replay the animation after the initial load)
    public $inlinedSvg = resource({
        params: () => ({svgUrl: this.$loadingSpinnerSvgUrl()}),
        loader: async ({params}) => {
            if (params.svgUrl) {
                if (!isAllowedSvgUrl(params.svgUrl)) {
                    return undefined
                }
                const raw = await firstValueFrom(this.httpClient.get(params.svgUrl, {responseType: "text"}))
                // DOMPurify strips scripts/event-handlers; bypass Angular's own sanitizer so it
                // preserves SVG animation attributes (animate, animateTransform, etc.).
                const clean = DOMPurify.sanitize(raw, {USE_PROFILES: {svg: true, svgFilters: true}})
                return this.sanitizer.bypassSecurityTrustHtml(clean) as string
            } else {
                return params.svgUrl
            }
        },
    })

    readonly httpClient = inject(HttpClient)
    readonly sanitizer = inject(DomSanitizer)
}
